import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone, MapPin, LogIn } from "lucide-react";
import { Link } from "react-router-dom";
import { LINKS, IMAGES } from "@/lib/assets";
import { useSettings } from "@/lib/SettingsContext";
import { AdminPanel } from "@/components/landing/AdminPanel";

export const Footer = () => {
  const CONTENT = useSettings();
  const c = CONTENT.contact;
  return (
    <footer id="contact" className="relative bg-white border-t border-slate-200" data-testid="footer">
      {/* Big CTA */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-28 md:py-36">
        <motion.p
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-xs tracking-[0.3em] uppercase text-amber-500 font-bold mb-6"
        >
          {c.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl text-navy-dark"
        >
          {c.headingLine1}<br /><span className="text-amber-500">{c.headingLine2}</span>
        </motion.h2>

        <div className="mt-12 flex flex-col sm:flex-row gap-4">
          <a
            href={LINKS.signup}
            className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors"
            data-testid="footer-join-btn"
          >
            {c.joinCta}
            <ArrowUpRight size={20} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a
            href={LINKS.login}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md border border-navy/25 text-navy-dark font-semibold hover:border-amber-500 hover:text-amber-500 transition-colors"
            data-testid="footer-login-btn"
          >
            <LogIn size={18} /> {c.loginCta}
          </a>
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 border-t border-slate-200 pt-10">
          <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-zinc-300 hover:text-amber-400 transition-colors" data-testid="contact-email">
            <Mail size={20} className="text-amber-500" /> {c.email}
          </a>
          <a href={`tel:${c.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-3 text-zinc-300 hover:text-amber-400 transition-colors" data-testid="contact-phone">
            <Phone size={20} className="text-amber-500" /> {c.phone}
          </a>
          <div className="flex items-center gap-3 text-zinc-300">
            <MapPin size={20} className="text-amber-500" /> {c.location}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="NTOA" className="h-10 w-10 object-contain" />
            <div className="leading-none">
              <div className="font-display font-black text-navy-dark tracking-tight">NTOA</div>
              <div className="text-[9px] tracking-[0.2em] text-zinc-500 uppercase">ntoassoc.com</div>
            </div>
          </div>
          <p className="text-xs text-zinc-500 text-center">
            © {new Date().getFullYear()} National Transportation Operators Association. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-zinc-500">
            <Link to="/privacy" className="hover:text-amber-500 transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-amber-500 transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/bylaws" className="hover:text-amber-500 transition-colors" data-testid="footer-bylaws-link">Bylaws</Link>
            <AdminPanel />
          </div>
        </div>
      </div>
    </footer>
  );
};
